import { Dictionary, groupBy } from 'lodash';

import { UpdateCampaignArchivedExperimentsInput } from '../actions/campaignsEdit';
import { RATE_CORRECTION_DEFAULT_VALUE } from '../constants';
import {
    getArchivedExperimentName,
    getExperimentsFilteredByArchiveParameter,
    isSegmentRateCorrectionEnabled
} from '../service/ExperimentsEditorHelpers/ExperimentsEditorHelpers';
import { getRandomId } from '../service/IdHelpers/IdHelpers';
import { getRateValueFromRateControlValue } from '../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import {
    CorrectionMapEditorValue,
    Experiment,
    ExperimentsConditionsValidationData,
    ExperimentsEditorData,
    ExperimentsValue,
    UpdateCampaignExperimentConditionsItem
} from '../typings/experimentsEditor';
import {
    CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_abSegmentRetargetingCondition_retargetingCondition,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_abSegmentStatisticRetargetingCondition_retargetingCondition_conditionRules,
    FindOrCreateExperimentsConditions_findOrCreateExperimentsConditions_addedConditions,
    GdCreateRetargetingConditionForExperimentsItemInput
} from '../typings/graphql-grid';
import { ABSegmentsRateCorrectionCondition } from '../typings/ratesCorrections';

import { getValueFromApiFormat } from './common/common';
import {
    getMultiplierFromRateCorrectionValue,
    getRateControlValueFromRateValue,
    getRateCorrectionValueFromMultiplier
} from './rateCorrections/rateCorrections';

export function getExperimentsWithArchivedMap(
    retargetingCondition: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_abSegmentRetargetingCondition_retargetingCondition
): Record<number, boolean> {
    return retargetingCondition.conditionRules.reduce((acc, experiment) => {
        const { sectionId } = experiment;

        if (sectionId !== null) {
            return {
                ...acc,
                [sectionId]: false
            };
        }

        return acc;
    }, {});
}

export function getGroupedByExperimentSegments(
    goals: CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals[] | null
): Dictionary<CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals[]> {
    if (goals === null) {
        return {};
    }

    const sortedGoals = goals.sort((segmentA, segmentB) => {
        return segmentA.name.localeCompare(segmentB.name);
    });

    return groupBy(sortedGoals, 'sectionId');
}

export function getCompleteArchivedMapFromApi(
    groupedSegments: Dictionary<CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals[]>,
    archivedExperimentsMap: Record<number, boolean>
): Record<number, boolean> {
    return Object.keys(archivedExperimentsMap).reduce((acc, experimentId) => {
        const experiment = groupedSegments[experimentId];
        // Если не пришло целей по данному эксперименту
        // то эксперимент считается заархивированным
        return experiment === undefined
            ? {
                  ...acc,
                  [experimentId]: true
              }
            : acc;
    }, archivedExperimentsMap);
}

export function mapExperimentsTree(
    groupedSegments: Dictionary<CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals[]>,
    archivedExperimentsMap: Record<number, boolean>
): Experiment[] {
    const experiments = Object.keys(groupedSegments).reduce((acc, experimentId) => {
        const experiment = groupedSegments[experimentId];

        return [
            ...acc,
            {
                id: experimentId,
                name: String(experiment[0].sectionName),
                inner: experiment.map(segment => {
                    return {
                        id: segment.id,
                        name: `${segment.name} - ${segment.percent}%`,
                        inner: null
                    };
                })
            }
        ];
    }, []);

    return Object.keys(archivedExperimentsMap).reduce((acc, experimentId) => {
        return archivedExperimentsMap[experimentId]
            ? [
                  ...acc,
                  {
                      id: experimentId,
                      name: getArchivedExperimentName(experimentId),
                      inner: null
                  }
              ]
            : acc;
    }, experiments);
}

export function mapSavedSelectedSegments(
    conditionRules: CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_abSegmentStatisticRetargetingCondition_retargetingCondition_conditionRules[]
): string[] {
    return conditionRules.reduce((acc, rule) => {
        return [...acc, ...rule.goals.map(goal => goal.id)];
    }, []);
}

export function getAvailableSegmentsMap(
    availableSegments: CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals[] | null
): Record<string, CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals> {
    if (availableSegments === null) {
        return {};
    }

    return availableSegments.reduce((acc, activeSegment) => {
        return {
            ...acc,
            [activeSegment.id]: activeSegment
        };
    }, {});
}

export function getCompleteSelectedMapFromApi(
    savedSelectedSegmentsIds: string[],
    availableSegmentsMap: Record<string, CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals>,
    experimentsTree: Experiment[],
    archivedExperimentsMap: Record<number, boolean>
): Record<number, boolean> {
    // Если пришел сохраненный активный сегмент, значит весь эксперимент активен
    // Обновляем selectedMap включенными родителями сегментов
    const activeExperimentsIds = new Set<string>();
    const newSelectedMap = savedSelectedSegmentsIds.reduce((acc, activeId) => {
        const segment = availableSegmentsMap[activeId];
        if (segment === undefined || segment.sectionId === null) {
            return acc;
        }

        const { sectionId } = segment;
        activeExperimentsIds.add(String(sectionId));
        // При перезапросе целей, необходимо создать selectedMap заново,
        // чтобы исключить цели удаленных счетчиков
        return {
            ...acc,
            [sectionId]: true,
            [activeId]: true
        };
    }, {});

    const inactiveExperiments = experimentsTree.filter(experiment => {
        return !activeExperimentsIds.has(experiment.id);
    });

    // Для всех неактивных экспериментов по умолчанию включаем все сегменты
    inactiveExperiments.forEach(experiment => {
        if (experiment.inner) {
            experiment.inner.forEach(segment => {
                newSelectedMap[segment.id] = true;
            });
        }
    });

    // Все архивированные эксперименты должны быть включены, потому что
    // единственная причина их показа - это их отключение
    Object.keys(archivedExperimentsMap).forEach(experimentId => {
        if (archivedExperimentsMap[experimentId]) {
            newSelectedMap[experimentId] = true;
        }
    });

    return newSelectedMap;
}

export function mapSavedABSegmentsCorrectionRates(
    rateCorrections: ABSegmentsRateCorrectionCondition[]
): Record<number, ABSegmentsRateCorrectionCondition> {
    return rateCorrections.reduce((acc, item) => {
        return {
            ...acc,
            [item.segmentId]: item
        };
    }, {});
}

export function getCompleteABSegmentsCorrectionRatesMapFromApi(
    availableSegmentsMap: Record<string, CampaignsABSegmentsQuery_client_abSegments_campaignGoals_goals>,
    savedCorrectionsMap: Record<number, ABSegmentsRateCorrectionCondition>
): Record<number, ABSegmentsRateCorrectionCondition> {
    // Почистить корректировки целей, которые перестали быть доступны
    // Из-за смены счетчика метрики
    const newCorrectionsMap = Object.keys(savedCorrectionsMap).reduce((acc, segmentId) => {
        const segment = availableSegmentsMap[segmentId];
        if (segment === undefined) {
            return acc;
        }

        return {
            ...acc,
            [segmentId]: savedCorrectionsMap[segmentId]
        };
    }, {});

    return Object.keys(availableSegmentsMap).reduce((acc, segmentId) => {
        if (acc[segmentId] === undefined) {
            acc[segmentId] = {
                id: getRandomId(),
                segmentId: availableSegmentsMap[segmentId].id,
                sectionId: availableSegmentsMap[segmentId].sectionId,
                value: RATE_CORRECTION_DEFAULT_VALUE
            };
        }

        return acc;
    }, newCorrectionsMap);
}

export function mapABSegmentsCorrectionsToEditor(
    correctionsMap: Record<number, ABSegmentsRateCorrectionCondition>
): Record<number, CorrectionMapEditorValue> {
    return Object.keys(correctionsMap).reduce((acc, nodeId) => {
        acc[nodeId] = {
            value: getRateControlValueFromRateValue(getRateCorrectionValueFromMultiplier(correctionsMap[nodeId].value)),
            errors: []
        };
        return acc;
    }, {});
}

export function mapABSegmentsCorrectionsToCampaignValue(
    savedCorrectionMap: Record<number, ABSegmentsRateCorrectionCondition>,
    correctionsMap: Record<number, CorrectionMapEditorValue>,
    selectedMap: Record<number, boolean>,
    isSegmentTargetingEnabled: boolean
): Record<number, ABSegmentsRateCorrectionCondition> {
    return Object.keys(correctionsMap).reduce((acc, nodeId) => {
        const isSegmentsRateCorrectionEnabled = isSegmentRateCorrectionEnabled(
            isSegmentTargetingEnabled,
            selectedMap[nodeId],
            selectedMap[savedCorrectionMap[nodeId].sectionId]
        );

        if (isSegmentsRateCorrectionEnabled) {
            return {
                ...acc,
                [nodeId]: {
                    ...acc[nodeId],
                    value: getMultiplierFromRateCorrectionValue(
                        getRateValueFromRateControlValue(correctionsMap[nodeId].value)
                    )
                }
            };
        }

        if (savedCorrectionMap[nodeId]) {
            return {
                ...acc,
                [nodeId]: {
                    ...acc[nodeId],
                    value: RATE_CORRECTION_DEFAULT_VALUE
                }
            };
        }

        return acc;
    }, savedCorrectionMap);
}

export function mapEditorExperimentsToCampaignExperiments(
    editorData: ExperimentsEditorData,
    experimentsValue: ExperimentsValue
): ExperimentsValue {
    return {
        // Удаление заархивированных экспериментов
        experimentsTree: getExperimentsFilteredByArchiveParameter(
            experimentsValue.experimentsTree,
            experimentsValue.archivedExperimentsMap,
            false
        ),
        selectedMap: editorData.selectedMap,
        areCorrectionsEnabled: editorData.areCorrectionsEnabled,
        isSegmentTargetingEnabled: editorData.isSegmentTargetingEnabled,
        correctionsMap: mapABSegmentsCorrectionsToCampaignValue(
            experimentsValue.correctionsMap,
            editorData.correctionsMap,
            editorData.selectedMap,
            editorData.isSegmentTargetingEnabled
        ),
        abSegmentRetargetingConditionId: experimentsValue.abSegmentRetargetingConditionId,
        abSegmentStatisticRetargetingConditionId: experimentsValue.abSegmentStatisticRetargetingConditionId,
        archivedExperimentsMap: editorData.archivedExperimentsMap,
        savedSelectedSegmentsIds: experimentsValue.savedSelectedSegmentsIds
    };
}

export function mapCreateExperimentsConditionsResult(
    campaignIds: string[],
    items: FindOrCreateExperimentsConditions_findOrCreateExperimentsConditions_addedConditions[]
): UpdateCampaignExperimentConditionsItem[] {
    return campaignIds.map((campaignId, index) => {
        const retargetingValues = items[index];

        return {
            campaignId,
            retargetingConditionId:
                retargetingValues === null
                    ? undefined
                    : getValueFromApiFormat(retargetingValues.retargetingConditionId),
            statisticRetargetingConditionId:
                retargetingValues === null
                    ? undefined
                    : getValueFromApiFormat(retargetingValues.statisticRetargetingConditionId)
        };
    });
}

export const mapExperimentsConditionValidationDataToUpdateArchivedExperimentsInput = (
    validationData: ExperimentsConditionsValidationData,
    campaignsIds: string[],
    experimentsConditionItems: GdCreateRetargetingConditionForExperimentsItemInput[]
): UpdateCampaignArchivedExperimentsInput[] => {
    const experimentsConditionsItemsMap = experimentsConditionItems.reduce((acc, conditionItem, index) => {
        return {
            ...acc,
            [campaignsIds[index]]: conditionItem
        };
    }, {});

    return Object.keys(validationData.campaigns).map(campaignId => {
        return {
            campaignId,
            archivedExperimentsIds: Object.keys(validationData.campaigns[campaignId].sectionIds).map(
                experimentIndex => {
                    return experimentsConditionsItemsMap[campaignId].sectionIds[Number(experimentIndex)];
                }
            )
        };
    });
};
